import { useState, useEffect, useMemo } from 'react';
import { Coins, Clock, Shield, Pencil, X, Check, Lock, Mail, MailOpen, MailX, BellOff, Phone, CheckCircle2, Plus, ClipboardList } from 'lucide-react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useAuth } from '../hooks/useAuth';
import { useProfileCompletion, useUpsertAttribute, useProfileAttributes, useMe } from '../hooks/useProfile';
import { usePointsLedger } from '../hooks/useRedemptions';
import ProfileStrengthRing from '../components/ProfileStrengthRing';
import NavShell from '../components/NavShell';
import { updateAvatar, updateEmailPreferences, smsOptIn, smsOptOut, fetchProfileScore, addEmail } from '../lib/api';
import { AVATARS, getAvatar } from '../lib/avatars';
import type { ProfileAttributeKey } from '@swipeopinion/shared';
import {
  EMPLOYMENT_FIELD_OPTIONS,
  HHI_FIELD_OPTIONS,
  AUTO_TYPE_FIELD_OPTIONS,
  AUTO_MAKE_FIELD_OPTIONS,
  PET_OWNERSHIP_FIELD_OPTIONS,
  MILITARY_STATUS_FIELD_OPTIONS,
  SMOKING_STATUS_FIELD_OPTIONS,
  INTERESTS_FIELD_OPTIONS,
  HOBBIES_FIELD_OPTIONS,
  LANGUAGE_FIELD_OPTIONS,
  SMARTPHONE_FIELD_OPTIONS,
  SOCIAL_MEDIA_FIELD_OPTIONS,
  ledgerLabel,
  TRUST_LEVEL_NAMES,
} from '@swipeopinion/shared';
import type { ProfileFieldOption } from '@swipeopinion/shared';

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function toDisplayLabel(value: string): string {
  return value.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());
}

function getDisplayName(me: ReturnType<typeof useMe>['data'], email: string): string {
  const name = me?.display_name ?? me?.member?.display_name;
  if (name) return name;
  return email.split('@')[0] ?? '';
}

function getInitials(me: ReturnType<typeof useMe>['data'], email: string): string {
  const name = me?.display_name ?? me?.member?.display_name;
  if (name) return name.charAt(0).toUpperCase();
  const local = email.split('@')[0] ?? '';
  const parts = local.split(/[._+-]/).filter(Boolean);
  if (parts.length >= 2) return (parts[0][0] + parts[1][0]).toUpperCase();
  return local.slice(0, 2).toUpperCase();
}

function formatBirthMonth(dob: string): string {
  const d = new Date(dob);
  if (isNaN(d.getTime())) return '';
  return d.toLocaleDateString('en-US', { month: 'long', year: 'numeric' });
}

function formatEmploymentShort(employment?: string): string | null {
  if (!employment) return null;
  const map: Record<string, string> = {
    employed_full: 'Full-Time', full_time: 'Full-Time',
    employed_part: 'Part-Time', part_time: 'Part-Time',
    self_employed: 'Self-Employed', contract: 'Contract / Freelance',
    unemployed: 'Unemployed',
    retired: 'Retired',
    student: 'Student',
    homemaker: 'Homemaker',
    none: 'None',
  };
  return map[employment] ?? toDisplayLabel(employment);
}

/**
 * Resolve a stored canonical `hhi` value to the nearest band's select value
 * so the dropdown reflects the member's current income tier. Handles numbers
 * that aren't exact band floors (e.g. a stored 250000 shows "$250,000 or more").
 */
function hhiToBandValue(stored: string | undefined): string {
  if (!stored) return '';
  const n = Number(stored);
  if (!Number.isFinite(n)) return '';
  // Negative values are the "prefer not to say" sentinel.
  if (n < 0) return '-1';
  // Pick the highest band whose lower bound is ≤ stored — matches onboarding's
  // "lower bound" encoding exactly for members who went through onboarding and
  // gracefully rounds any raw legacy values down to the nearest band.
  let best = HHI_FIELD_OPTIONS[0]!;
  for (const b of HHI_FIELD_OPTIONS) {
    const bv = Number(b.value);
    if (bv >= 0 && bv <= n) best = b;
  }
  return best.value;
}

function formatHomeShort(value?: string): string | null {
  if (!value) return null;
  if (value === 'own') return 'Homeowner';
  if (value === 'rent') return 'Renter';
  return null;
}

import { zipToCityState } from '../data/zip3ToCity';

// Derive state abbreviation from the first two digits of a ZIP when the
// `state` attribute isn't already populated on the member.
const ZIP_STATES: Record<string, string> = {
  '01': 'MA', '02': 'MA', '03': 'NH', '04': 'ME', '05': 'VT', '06': 'CT',
  '07': 'NJ', '08': 'NJ', '10': 'NY', '11': 'NY', '12': 'NY', '13': 'NY', '14': 'NY',
  '15': 'PA', '16': 'PA', '17': 'PA', '18': 'PA', '19': 'PA',
  '20': 'DC', '21': 'MD', '22': 'VA', '23': 'VA', '24': 'VA', '25': 'WV', '26': 'WV',
  '27': 'NC', '28': 'NC', '29': 'SC', '30': 'GA', '31': 'GA',
  '32': 'FL', '33': 'FL', '34': 'FL',
  '35': 'AL', '36': 'AL', '37': 'TN', '38': 'TN',
  '39': 'MS', '40': 'KY', '41': 'KY', '42': 'KY',
  '43': 'OH', '44': 'OH', '45': 'OH',
  '46': 'IN', '47': 'IN', '48': 'MI', '49': 'MI',
  '50': 'IA', '51': 'IA', '52': 'IA', '53': 'WI', '54': 'WI',
  '55': 'MN', '56': 'MN', '57': 'SD', '58': 'ND', '59': 'MT',
  '60': 'IL', '61': 'IL', '62': 'IL', '63': 'MO', '64': 'MO', '65': 'MO',
  '66': 'KS', '67': 'KS', '68': 'NE', '69': 'NE',
  '70': 'LA', '71': 'LA', '72': 'AR', '73': 'OK', '74': 'OK',
  '75': 'TX', '76': 'TX', '77': 'TX', '78': 'TX', '79': 'TX',
  '80': 'CO', '81': 'CO', '82': 'WY', '83': 'ID', '84': 'UT',
  '85': 'AZ', '86': 'AZ', '87': 'NM', '88': 'NM',
  '89': 'NV', '90': 'CA', '91': 'CA', '92': 'CA', '93': 'CA', '94': 'CA', '95': 'CA', '96': 'HI',
  '97': 'OR', '98': 'WA', '99': 'AK',
};

/**
 * Resolve a ZIP to a "City, State" label via the ZIP3 SCF lookup. Falls back
 * to the member's stored `state` attribute, then to a derived state from the
 * ZIP prefix when neither yields a city. Returns null if no zip is provided.
 */
function zipToLocation(zip: string, state?: string): string | null {
  const stateFallback = state ?? ZIP_STATES[zip.slice(0, 2)];
  return zipToCityState(zip, stateFallback);
}

// ---------------------------------------------------------------------------
// Sub-components
// ---------------------------------------------------------------------------

/**
 * "Tap to confirm" affordance for inferred (system-guessed) field values.
 * Confirming saves the value explicitly (the normal save path), which makes it
 * count toward completion. Confirmation is always a member action — the value
 * is never auto-flipped to explicit; the point is the member verifies the guess.
 */
function ConfirmHint({ attrKey, onConfirm }: { attrKey: string; onConfirm: () => void }) {
  return (
    <button
      type="button"
      data-testid={`confirm-inferred-${attrKey}`}
      onClick={onConfirm}
      title="We filled this in from your answers — tap to confirm"
      className="text-[11px] font-medium text-amber-600 hover:text-amber-700 whitespace-nowrap"
    >
      ⚠ Confirm
    </button>
  );
}

interface FieldRowProps {
  label: string;
  attrKey: ProfileAttributeKey;
  type?: 'text' | 'number' | 'date' | 'select';
  options?: { value: string; label: string }[];
  onSave: (key: ProfileAttributeKey, value: string) => void;
  inputProps?: React.InputHTMLAttributes<HTMLInputElement>;
  initialValue?: string;
  locked?: boolean;
  /** value came from source='inferred' — show the confirm affordance. */
  unconfirmed?: boolean;
}

// Exported for focused unit testing of the confirm-inferred affordance.
export function FieldRow({ label, attrKey, type = 'text', options = [], onSave, inputProps, initialValue, locked, unconfirmed }: FieldRowProps) {
  const [editing, setEditing] = useState(false);
  const [val, setVal] = useState(initialValue ?? '');

  useEffect(() => {
    if (initialValue !== undefined) setVal(initialValue);
  }, [initialValue]);

  function commit(value: string) {
    if (value.trim()) onSave(attrKey, value.trim());
    setEditing(false);
  }

  if (locked) {
    // Use option label if available, otherwise format the raw value
    const displayVal = val
      ? (options.find((o) => o.value === val)?.label ?? toDisplayLabel(val))
      : 'Not set';
    return (
      <div className="flex items-center justify-between py-3 border-b border-gray-50 last:border-0">
        <span className="text-sm text-[#6B7280] w-40 flex items-center gap-1">
          <Lock className="w-3 h-3 text-[#9CA3AF]" />
          {label}
        </span>
        <span className="text-sm text-[#111827]" title="Locked after registration for survey accuracy">
          {displayVal}
        </span>
      </div>
    );
  }

  if (type === 'select') {
    return (
      <div className="flex items-center justify-between py-3 border-b border-gray-50 last:border-0">
        <span className="text-sm text-[#6B7280] w-40">{label}</span>
        <div className="flex items-center gap-2">
          {unconfirmed && val && <ConfirmHint attrKey={attrKey} onConfirm={() => onSave(attrKey, val)} />}
          <select
            value={val}
            className="text-sm border border-[#E5E7EB] rounded-xl px-3 py-1.5 bg-white focus:outline-none focus:border-[#5B4FE8] focus:ring-2 focus:ring-purple-100"
            onChange={(e) => { setVal(e.target.value); if (e.target.value) onSave(attrKey, e.target.value); }}
          >
            <option value="">-- not set --</option>
            {options.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
          </select>
        </div>
      </div>
    );
  }

  return (
    <div className="flex items-center justify-between py-3 border-b border-gray-50 last:border-0">
      <span className="text-sm text-[#6B7280] w-40">{label}</span>
      {editing ? (
        <input
          autoFocus type={type} value={val}
          onChange={(e) => setVal(e.target.value)}
          onBlur={(e) => commit(e.target.value)}
          onKeyDown={(e) => { if (e.key === 'Enter') commit(val); if (e.key === 'Escape') setEditing(false); }}
          className="text-sm border border-[#5B4FE8] rounded-xl px-3 py-1.5 w-48 focus:outline-none focus:ring-2 focus:ring-purple-100"
          {...inputProps}
        />
      ) : (
        <div className="flex items-center gap-2">
          {unconfirmed && val && <ConfirmHint attrKey={attrKey} onConfirm={() => onSave(attrKey, val)} />}
          <button onClick={() => setEditing(true)} className="text-sm text-[#6B7280] hover:text-[#5B4FE8] px-2 py-1 rounded transition-colors">
            {val ? toDisplayLabel(val) : '+ Add'}
          </button>
        </div>
      )}
    </div>
  );
}

interface MultiSelectRowProps {
  label: string;
  attrKey: ProfileAttributeKey;
  options: { value: string; label: string }[];
  onSave: (key: ProfileAttributeKey, value: string) => void;
  initialValue?: string;
  /** value came from source='inferred' — show the confirm affordance. */
  unconfirmed?: boolean;
}

function parseMultiValue(raw: string | undefined): string[] {
  if (!raw) return [];
  // Try JSON array first, then comma-separated
  try {
    const parsed = JSON.parse(raw);
    if (Array.isArray(parsed)) return parsed;
  } catch { /* not JSON */ }
  return raw.split(',').map((s) => s.trim()).filter(Boolean);
}

// Exported for focused unit testing of the confirm-inferred affordance.
export function MultiSelectRow({ label, attrKey, options, onSave, initialValue, unconfirmed }: MultiSelectRowProps) {
  const [selected, setSelected] = useState<string[]>(() => parseMultiValue(initialValue));

  useEffect(() => {
    if (initialValue !== undefined) setSelected(parseMultiValue(initialValue));
  }, [initialValue]);

  function toggle(val: string) {
    let next: string[];
    if (val === 'none') {
      next = selected.includes('none') ? [] : ['none'];
    } else {
      const without = selected.filter((s) => s !== 'none');
      next = without.includes(val) ? without.filter((s) => s !== val) : [...without, val];
    }
    setSelected(next);
    if (next.length > 0) onSave(attrKey, next.join(','));
  }

  return (
    <div className="py-3 border-b border-gray-50 last:border-0">
      <div className="flex items-center justify-between mb-2">
        <span className="text-sm text-[#6B7280]">{label}</span>
        {unconfirmed && selected.length > 0 && (
          <ConfirmHint attrKey={attrKey} onConfirm={() => onSave(attrKey, selected.join(','))} />
        )}
      </div>
      <div className="flex flex-wrap gap-2">
        {options.map((opt) => (
          <button
            key={opt.value} type="button" onClick={() => toggle(opt.value)}
            className={`px-3 py-1.5 rounded-full text-xs font-medium border transition-colors ${
              selected.includes(opt.value)
                ? 'bg-[#5B4FE8] text-white border-[#5B4FE8]'
                : 'border-[#E5E7EB] text-[#6B7280] hover:border-[#5B4FE8]'
            }`}
          >{opt.label}</button>
        ))}
      </div>
    </div>
  );
}

function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div className="bg-white rounded-2xl shadow-sm p-6">
      <h3 className="text-sm font-bold text-[#111827] uppercase tracking-wide mb-3">{title}</h3>
      {children}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Avatar Selection Modal
// ---------------------------------------------------------------------------

function AvatarModal({ currentId, onSelect, onClose }: {
  currentId: string | null; onSelect: (id: string) => void; onClose: () => void;
}) {
  const [selected, setSelected] = useState<string | null>(currentId);
  const [saving, setSaving] = useState(false);

  async function handleSave() {
    if (!selected) return;
    setSaving(true);
    await onSelect(selected);
    setSaving(false);
    onClose();
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-sm">
      <div className="bg-white rounded-2xl shadow-xl w-full max-w-md mx-4 p-6 space-y-5">
        <div className="flex items-center justify-between">
          <h3 className="text-lg font-bold text-[#111827]">Choose Your Avatar</h3>
          <button onClick={onClose} className="text-[#6B7280] hover:text-[#111827]"><X className="w-5 h-5" /></button>
        </div>

        <div className="grid grid-cols-4 gap-3">
          {AVATARS.map((a) => (
            <button
              key={a.id}
              onClick={() => setSelected(a.id)}
              className={`flex flex-col items-center gap-1 p-3 rounded-2xl border-2 transition-all ${
                selected === a.id
                  ? 'border-[#5B4FE8] bg-[#EEF2FF]'
                  : 'border-transparent hover:border-[#E5E7EB]'
              }`}
            >
              <div className={`w-12 h-12 rounded-full ${a.bg} flex items-center justify-center text-2xl relative`}>
                {a.emoji}
                {selected === a.id && (
                  <div className="absolute -bottom-0.5 -right-0.5 w-5 h-5 rounded-full bg-[#5B4FE8] flex items-center justify-center">
                    <Check className="w-3 h-3 text-white" />
                  </div>
                )}
              </div>
              <span className="text-[10px] font-medium text-[#6B7280]">{a.name}</span>
            </button>
          ))}
        </div>

        <button
          onClick={() => void handleSave()}
          disabled={!selected || saving}
          className="w-full py-3 rounded-full bg-[#5B4FE8] text-white font-medium text-sm hover:bg-[#4338CA] disabled:opacity-50 transition-colors"
        >
          {saving ? 'Saving...' : 'Save'}
        </button>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Email Preferences Section
// ---------------------------------------------------------------------------

const EMAIL_FREQ_OPTIONS = [
  { value: 'daily', label: 'Daily', Icon: MailOpen },
  { value: 'few_times_week', label: 'A few times a week', Icon: Mail },
  { value: 'weekly', label: 'Weekly', Icon: MailX },
  { value: 'never', label: 'Never', Icon: BellOff },
] as const;

function EmailPreferencesSection({ currentFrequency }: { currentFrequency: string }) {
  const [selected, setSelected] = useState(currentFrequency);
  const [saving, setSaving] = useState(false);
  const queryClient = useQueryClient();

  useEffect(() => { setSelected(currentFrequency); }, [currentFrequency]);

  async function handleChange(value: string) {
    setSelected(value);
    setSaving(true);
    try {
      await updateEmailPreferences(value);
      void queryClient.invalidateQueries({ queryKey: ['me'] });
    } catch { /* non-critical */ }
    setSaving(false);
  }

  return (
    <Section title="Email Preferences">
      <p className="text-sm text-[#6B7280] mb-3">How often would you like survey invitation emails?</p>
      <div className="flex flex-wrap gap-2">
        {EMAIL_FREQ_OPTIONS.map(({ value, label, Icon }) => (
          <button
            key={value}
            onClick={() => void handleChange(value)}
            disabled={saving}
            className={`flex items-center gap-2 px-4 py-2.5 rounded-full text-sm font-medium border-2 transition-all ${
              selected === value
                ? 'bg-[#5B4FE8] text-white border-[#5B4FE8]'
                : 'bg-white text-[#6B7280] border-[#E5E7EB] hover:border-[#5B4FE8]/50'
            }`}
          >
            <Icon className="w-4 h-4" />
            {label}
          </button>
        ))}
      </div>
    </Section>
  );
}

// Shown INSTEAD of email-frequency for members with no email on file (phone
// registration — email='' or null). Lets them add one; only after that does the
// frequency picker appear (email-frequency is meaningless without an address).
export function AddEmailSection() {
  const [email, setEmail] = useState('');
  const [saving, setSaving] = useState(false);
  const [done, setDone] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const queryClient = useQueryClient();

  async function submit() {
    if (!email.trim()) return;
    setSaving(true); setError(null);
    try {
      await addEmail(email.trim());
      setDone(true);
      void queryClient.invalidateQueries({ queryKey: ['me'] });
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to add email');
    }
    setSaving(false);
  }

  return (
    <Section title="Email Preferences">
      <p className="text-sm text-[#6B7280] mb-3">Add your email address to get survey invitations and account updates.</p>
      {done ? (
        <p className="text-sm text-[#10B981] font-medium">Email added — check your inbox to confirm.</p>
      ) : (
        <div className="flex flex-wrap items-center gap-2">
          <input
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="you@example.com"
            className="flex-1 min-w-[180px] border-2 border-[#E5E7EB] rounded-full px-4 py-2.5 text-sm focus:border-[#5B4FE8] outline-none"
          />
          <button
            onClick={() => void submit()}
            disabled={saving || !email.trim()}
            className="px-5 py-2.5 rounded-full bg-[#5B4FE8] text-white text-sm font-medium disabled:opacity-50"
          >
            Add email
          </button>
        </div>
      )}
      {error && <p className="text-sm text-[#EF4444] mt-2">{error}</p>}
    </Section>
  );
}

// ---------------------------------------------------------------------------
// Phone Number Section
// ---------------------------------------------------------------------------

function SmsConsentModal({
  onAgree,
  onCancel,
  submitting,
  error,
}: {
  onAgree: () => void;
  onCancel: () => void;
  submitting: boolean;
  error: string | null;
}) {
  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-sm p-4"
      onClick={onCancel}
    >
      <div
        className="bg-white rounded-2xl shadow-xl w-full max-w-sm p-6 space-y-4"
        onClick={(e) => e.stopPropagation()}
        data-testid="sms-consent-modal"
      >
        <h3 className="text-lg font-bold font-display text-[#111827]">Enable SMS notifications</h3>
        <p className="text-sm text-[#6B7280] leading-relaxed">
          By enabling SMS notifications, you agree to receive text messages from Swipe
          Opinion at your verified phone number. Message and data rates may apply. Reply
          STOP at any time to unsubscribe.
        </p>
        {error && <p className="text-xs text-red-600">{error}</p>}
        <div className="flex gap-2">
          <button
            type="button"
            onClick={onCancel}
            disabled={submitting}
            className="flex-1 rounded-full border border-[#E5E7EB] py-2.5 text-sm font-medium text-[#111827] hover:bg-gray-50 disabled:opacity-60"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={onAgree}
            disabled={submitting}
            className="flex-1 rounded-full bg-[#5B4FE8] py-2.5 text-sm font-semibold text-white hover:bg-[#4338CA] disabled:opacity-60"
          >
            {submitting ? 'Enabling…' : 'I Agree'}
          </button>
        </div>
      </div>
    </div>
  );
}

function PhoneSection({ phoneNumber, phoneVerified, smsOptedIn }: { phoneNumber: string | null; phoneVerified: boolean; smsOptedIn: boolean }) {
  const [optedIn, setOptedIn] = useState(smsOptedIn);
  const [saving, setSaving] = useState(false);
  const [consentOpen, setConsentOpen] = useState(false);
  const [consentError, setConsentError] = useState<string | null>(null);
  const [toast, setToast] = useState<string | null>(null);
  const queryClient = useQueryClient();

  useEffect(() => { setOptedIn(smsOptedIn); }, [smsOptedIn]);

  function formatDisplay(phone: string): string {
    const d = phone.replace(/^\+1/, '');
    if (d.length === 10) return `(${d.slice(0, 3)}) ${d.slice(3, 6)}-${d.slice(6)}`;
    return phone;
  }

  function showToast(msg: string) {
    setToast(msg);
    setTimeout(() => setToast(null), 2500);
  }

  function handleToggleClick() {
    if (saving) return;
    if (optedIn) {
      void handleOptOut();
    } else {
      setConsentError(null);
      setConsentOpen(true);
    }
  }

  async function handleOptOut() {
    setSaving(true);
    const prev = optedIn;
    try {
      await smsOptOut();
      setOptedIn(false);
      showToast('SMS notifications turned off');
      void queryClient.invalidateQueries({ queryKey: ['me'] });
    } catch (err) {
      setOptedIn(prev);
      showToast(err instanceof Error ? err.message : 'Failed to turn off SMS');
    } finally {
      setSaving(false);
    }
  }

  async function handleOptInAgree() {
    setSaving(true);
    setConsentError(null);
    try {
      await smsOptIn();
      setOptedIn(true);
      setConsentOpen(false);
      showToast('SMS notifications enabled');
      void queryClient.invalidateQueries({ queryKey: ['me'] });
    } catch (err) {
      setConsentError(err instanceof Error ? err.message : 'Could not enable SMS.');
    } finally {
      setSaving(false);
    }
  }

  // When the phone isn't verified, hide the SMS toggle entirely — the user
  // verifies phone via the Trust Center flow first.
  if (!phoneNumber || !phoneVerified) {
    return (
      <Section title="Phone Number">
        <p className="text-sm text-[#6B7280]">
          No verified phone number on file. Verify your phone from the Trust Center to
          unlock SMS notifications.
        </p>
      </Section>
    );
  }

  return (
    <Section title="Phone Number">
      <div className="space-y-2">
        <div className="flex items-center justify-between py-2">
          <div className="flex items-center gap-2">
            <Phone className="w-4 h-4 text-[#6B7280]" />
            <span className="text-sm text-[#111827]">{formatDisplay(phoneNumber)}</span>
            <span className="inline-flex items-center gap-1 text-xs text-[#10B981]">
              <CheckCircle2 className="w-3 h-3" /> Verified
            </span>
          </div>
        </div>
        <div className="flex items-center justify-between py-2 border-t border-gray-50">
          <div>
            <p className="text-sm text-[#111827]">SMS notifications</p>
            <p className="text-xs text-[#6B7280]">
              {optedIn
                ? 'Receiving texts about high-value surveys'
                : 'Get texts about high-value surveys'}
            </p>
          </div>
          <button
            type="button"
            onClick={handleToggleClick}
            disabled={saving}
            aria-pressed={optedIn}
            aria-label="SMS notifications"
            className={`relative w-12 h-7 rounded-full transition-colors ${
              optedIn ? 'bg-[#5B4FE8]' : 'bg-[#D1D5DB]'
            } disabled:opacity-60`}
          >
            <span className={`absolute top-0.5 left-0.5 w-6 h-6 bg-white rounded-full shadow transition-transform ${
              optedIn ? 'translate-x-5' : ''
            }`} />
          </button>
        </div>
      </div>

      {toast && (
        <div className="mt-2 text-xs text-[#10B981]" role="status">{toast}</div>
      )}

      {consentOpen && (
        <SmsConsentModal
          onAgree={() => void handleOptInAgree()}
          onCancel={() => { setConsentOpen(false); setConsentError(null); }}
          submitting={saving}
          error={consentError}
        />
      )}
    </Section>
  );
}

// ---------------------------------------------------------------------------
// Children Manager
// ---------------------------------------------------------------------------

interface ChildEntry { birth_year: number; gender: string }

// A member has an email on file only when it's a non-empty string. Phone
// registration leaves email='' (empty string, NOT null) — treat '' AND
// null/undefined as "no email" so the profile hides email-frequency and offers
// an add-email CTA instead. (An `=== null` check would miss the '' case.)
export function memberHasEmail(email: string | null | undefined): boolean {
  return typeof email === 'string' && email.trim() !== '';
}

// Car Make is meaningless when the member has no vehicle ('none') — hide it.
export function showsCarMake(vehicleValue: string | undefined): boolean {
  return vehicleValue !== 'none';
}

export function ChildrenManager({ initialValue, fallback, onSave }: { initialValue: string | undefined; fallback?: ChildEntry[]; onSave: (key: ProfileAttributeKey, value: string) => void }) {
  const [children, setChildren] = useState<ChildEntry[]>(() => {
    if (initialValue) {
      try { const parsed = JSON.parse(initialValue); if (Array.isArray(parsed) && parsed.length > 0) return parsed; } catch { /* noop */ }
    }
    // Seed from child_profile rows (surfaced via /me) when the
    // child_birth_years attribute is empty — a freshly-onboarded member's kids
    // live only in the child_profile table, so the editor rendered blank.
    return fallback && fallback.length > 0 ? fallback : [];
  });

  const currentYear = new Date().getFullYear();

  function save(updated: ChildEntry[]) {
    setChildren(updated);
    // B-BL-6 (2026-05-09) — children_count write removed: the value is
    // never displayed and went stale on profile edits anyway. The async
    // qualification matcher derives childCount from ChildProfile.count(),
    // and child_birth_years remains the source of truth for the UI.
    onSave('child_birth_years' as ProfileAttributeKey, JSON.stringify(updated));
  }

  function addChild() {
    if (children.length >= 6) return;
    save([...children, { birth_year: 0, gender: '' }]);
  }

  function updateChild(idx: number, field: string, value: string | number) {
    save(children.map((c, i) => i === idx ? { ...c, [field]: value } : c));
  }

  function removeChild(idx: number) {
    save(children.filter((_, i) => i !== idx));
  }

  return (
    <div className="py-3 border-b border-gray-50 last:border-0">
      <span className="text-sm text-[#6B7280] block mb-2">Children</span>
      <div className="space-y-2">
        {children.map((child, idx) => (
          <div key={idx} className="flex items-center gap-2 bg-[#F8F9FA] rounded-xl p-2">
            <select
              value={child.birth_year || ''}
              onChange={(e) => updateChild(idx, 'birth_year', parseInt(e.target.value))}
              className="flex-1 border border-[#E5E7EB] rounded-lg px-2 py-1.5 text-sm"
            >
              <option value="">Birth year</option>
              {Array.from({ length: 18 }, (_, i) => { const y = currentYear - i; return <option key={y} value={y}>{y} ({i} yrs)</option>; })}
            </select>
            <select
              value={child.gender}
              onChange={(e) => updateChild(idx, 'gender', e.target.value)}
              className="w-24 border border-[#E5E7EB] rounded-lg px-2 py-1.5 text-sm"
            >
              <option value="">Gender</option>
              <option value="boy">Boy</option>
              <option value="girl">Girl</option>
              <option value="other">Other</option>
            </select>
            <button onClick={() => removeChild(idx)} className="text-[#EF4444] p-1"><X className="w-4 h-4" /></button>
          </div>
        ))}
        {children.length < 6 && (
          <button onClick={addChild} className="text-sm text-[#5B4FE8] font-medium flex items-center gap-1">
            <Plus className="w-4 h-4" /> Add a child
          </button>
        )}
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Chip Picker (multi-select, max items)
// ---------------------------------------------------------------------------

// Parse a stored member_attribute multi_select value into canonical
// codes. The 'none' sentinel (an explicit "none of the above" answer)
// is dropped to an empty selection — the None pill is the UI affordance
// for that state, never a real chip value.
function parseChipValue(v: string | undefined): string[] {
  if (!v) return [];
  return v.split(',').map((s) => s.trim()).filter((s) => s !== '' && s !== 'none');
}

function ChipPicker({ label, options, max, attrKey, initialValue, onSave }: {
  label: string; options: ProfileFieldOption[]; max: number; attrKey: ProfileAttributeKey;
  initialValue: string | undefined; onSave: (key: ProfileAttributeKey, value: string) => void;
}) {
  // `selected` holds canonical attribute VALUES (e.g. 'technology'),
  // matching exactly what member_attribute stores — so pill highlight
  // (selected.includes(opt.value)) and the saved payload both round-trip.
  const [selected, setSelected] = useState<string[]>(() => parseChipValue(initialValue));

  useEffect(() => {
    if (initialValue !== undefined) {
      setSelected(parseChipValue(initialValue));
    }
  }, [initialValue]);

  function toggle(value: string) {
    let next: string[];
    if (selected.includes(value)) {
      next = selected.filter((s) => s !== value);
    } else {
      if (selected.length >= max) return;
      next = [...selected, value];
    }
    setSelected(next);
    onSave(attrKey, next.join(','));
  }

  function clearAll() {
    if (selected.length === 0) return;
    setSelected([]);
    onSave(attrKey, '');
  }

  return (
    <div className="py-3 border-b border-gray-50 last:border-0">
      <div className="flex items-center justify-between mb-2">
        <span className="text-sm text-[#6B7280]">{label}</span>
        <span className="text-xs text-[#9CA3AF]">{selected.length}/{max}</span>
      </div>
      <div className="flex flex-wrap gap-2">
        {options.map((opt) => (
          <button
            key={opt.value}
            type="button"
            onClick={() => toggle(opt.value)}
            className={`px-3 py-1.5 rounded-full text-xs font-medium border transition-colors ${
              selected.includes(opt.value)
                ? 'bg-[#5B4FE8] text-white border-[#5B4FE8]'
                : 'border-[#E5E7EB] text-[#6B7280] hover:border-[#5B4FE8]'
            }`}
          >
            {opt.label}
          </button>
        ))}
        <button
          type="button"
          onClick={clearAll}
          className={`px-3 py-1.5 rounded-full text-xs font-medium border transition-colors ${
            selected.length === 0
              ? 'bg-[#5B4FE8] text-white border-[#5B4FE8]'
              : 'border-[#E5E7EB] text-[#6B7280] hover:border-[#5B4FE8]'
          }`}
        >
          None
        </button>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Profile score bar — tiered incentive surface
// ---------------------------------------------------------------------------
//
// Onboarding incentive redesign (2026-05-11). Reads GET /api/profile/score
// and shows a slim progress bar with the current %. The bar is purely
// visual feedback here — the dashboard's ProfileScoreCard owns the
// next-tier CTA copy, this page just confirms progress.

function ProfileScoreBar() {
  const { data } = useQuery({
    queryKey: ['profile', 'score'],
    queryFn: fetchProfileScore,
    staleTime: 60_000,
  });

  if (!data) return null;
  const pct = Math.round(data.score * 100);
  const color =
    data.score >= 0.9 ? 'bg-green-500'
    : data.score >= 0.65 ? 'bg-amber-500'
    : 'bg-[#5B4FE8]';

  return (
    <div className="bg-white rounded-2xl shadow-sm p-4">
      <div className="flex items-center justify-between mb-2">
        <span className="text-sm font-medium text-gray-700">Profile Strength</span>
        <span className="text-sm font-semibold text-gray-900 tabular-nums">{pct}%</span>
      </div>
      <div className="relative h-2 bg-gray-100 rounded-full overflow-hidden">
        <div
          className={`absolute inset-y-0 left-0 ${color} rounded-full transition-all duration-300`}
          style={{ width: `${Math.min(100, pct)}%` }}
        />
      </div>
      <p className="mt-2 text-xs text-gray-500">
        {data.filled} of {data.denominator} fields — {pct}% complete
        {data.score >= 0.9 ? ' · top tier reached.' :
         data.score >= 0.65 ? ' · Tier 3 (+40 pts) unlocks at 90%.' :
         ' · Tier 2 (+35 pts) unlocks at 65%.'}
      </p>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Page
// ---------------------------------------------------------------------------

const today = new Date();
const maxDOB = new Date(today.getFullYear() - 18, today.getMonth(), today.getDate()).toISOString().split('T')[0];
const minDOB = new Date(today.getFullYear() - 100, today.getMonth(), today.getDate()).toISOString().split('T')[0];

const RACE_OPTIONS = [
  { value: 'White', label: 'White' },
  { value: 'Black or African American', label: 'Black or African American' },
  { value: 'Hispanic or Latino', label: 'Hispanic or Latino' },
  { value: 'Asian', label: 'Asian' },
  { value: 'American Indian or Alaska Native', label: 'American Indian or Alaska Native' },
  { value: 'Native Hawaiian or Pacific Islander', label: 'Native Hawaiian or Pacific Islander' },
  { value: 'Multi-ethnic', label: 'Multi-ethnic' },
  { value: 'Prefer not to answer', label: 'Prefer not to answer' },
];

export default function ProfilePage() {
  const { session } = useAuth();
  const { data: me } = useMe();
  // Trust level/name from the canonical /api/me trust_level (getTrustLevel),
  // mapped via the shared TRUST_LEVEL_NAMES table — no local label copy.
  const trustLevel = (me as { trust_level?: number } | undefined)?.trust_level ?? 1;
  const trustLevelName = TRUST_LEVEL_NAMES[trustLevel] ?? 'Basic';
  const queryClient = useQueryClient();
  const { data: completion, isLoading } = useProfileCompletion();
  const { mutate: saveAttr } = useUpsertAttribute();
  const { data: attributes } = useProfileAttributes();
  const { data: ledger } = usePointsLedger(1);

  const attrMap = useMemo(() => {
    if (!attributes) return {} as Record<string, string>;
    return Object.fromEntries(attributes.map((a) => [a.key, a.value]));
  }, [attributes]);

  // source per key — drives the "tap to confirm" affordance on inferred fields
  // (system guesses the member hasn't verified, which don't count toward
  // completion until confirmed via an explicit save).
  const attrSourceMap = useMemo(() => {
    if (!attributes) return {} as Record<string, string>;
    return Object.fromEntries(attributes.map((a) => [a.key, a.source]));
  }, [attributes]);
  const isInferred = (key: string): boolean => attrSourceMap[key] === 'inferred';

  const [avatarModalOpen, setAvatarModalOpen] = useState(false);

  const hasChildren = attrMap['has_children'];
  const initials = getInitials(me, session?.user.email ?? '');
  const displayName = getDisplayName(me, session?.user.email ?? '');
  const balance = me?.points_balance ?? 0;
  const lifetimePoints = (me as Record<string, unknown>)?.points_lifetime as number ?? balance;
  const recentActivity = ledger?.entries.slice(0, 3) ?? [];

  const avatarId = me?.avatar_id ?? me?.member?.avatar_id ?? null;
  const avatar = getAvatar(avatarId);

  function handleSave(key: ProfileAttributeKey, value: string) {
    saveAttr({ key, value });
  }

  async function handleAvatarSelect(id: string) {
    await updateAvatar(id);
    void queryClient.invalidateQueries({ queryKey: ['me'] });
  }

  // Build condensed demographic line for hero
  const demoItems: string[] = [];
  const zip = attrMap['zip_code'];
  const state = attrMap['state'];
  if (zip) {
    const loc = zipToLocation(zip, state);
    if (loc) demoItems.push(loc);
  }
  const dob = attrMap['date_of_birth'];
  if (dob) {
    const bm = formatBirthMonth(dob);
    if (bm) demoItems.push(bm);
  }
  const gender = attrMap['gender'];
  if (gender && gender !== 'prefer_not_to_say') demoItems.push(toDisplayLabel(gender));
  const emp = formatEmploymentShort(attrMap['employment_status'] ?? attrMap['employment']);
  if (emp) demoItems.push(emp);
  const home = formatHomeShort(attrMap['home_ownership']);
  if (home) demoItems.push(home);
  if (attrMap['has_children'] === 'yes') demoItems.push('Has Kids');

  return (
    <NavShell>
      <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.2 }} className="px-4 sm:px-6 py-6 space-y-6">
        {/* Hero banner */}
        <div className="relative overflow-hidden rounded-2xl bg-gradient-to-r from-[#5B4FE8] to-teal-500 p-6 sm:p-8 text-white">
          <div className="absolute top-0 right-0 w-64 h-64 bg-white/5 rounded-full -translate-y-1/2 translate-x-1/3" />
          <div className="relative flex items-center gap-5">
            <div className="relative">
              <div className={`w-20 h-20 rounded-full flex items-center justify-center text-3xl font-bold flex-shrink-0 ${
                avatar ? avatar.bg : 'bg-white/20'
              }`}>
                {avatar ? avatar.emoji : initials}
              </div>
              <button
                onClick={() => setAvatarModalOpen(true)}
                className="absolute -bottom-1 -right-1 w-7 h-7 rounded-full bg-white text-[#5B4FE8] flex items-center justify-center shadow-md hover:scale-110 transition-transform"
              >
                <Pencil className="w-3.5 h-3.5" />
              </button>
            </div>
            <div>
              <h1 className="text-2xl font-bold font-display">{displayName}</h1>
              <p className="text-purple-200 text-sm">{session?.user.email}</p>
              <div className="flex flex-wrap gap-2 mt-2">
                <span className="text-xs font-medium bg-white/20 px-3 py-1 rounded-full">Basic Member</span>
                <span className="text-xs font-medium bg-white/20 px-3 py-1 rounded-full">
                  Joined {me?.member?.created_at ? new Date(me.member.created_at).toLocaleDateString('en-US', { month: 'short', year: 'numeric' }) : ''}
                </span>
              </div>
              {demoItems.length >= 2 && (
                <p className="text-xs text-white/70 mt-2">
                  {demoItems.join('  \u2022  ')}
                </p>
              )}
            </div>
          </div>
        </div>

        {/* Tiered profile score (2026-05-11 incentive redesign) */}
        <ProfileScoreBar />

        {/* Stat cards */}
        <div className="grid grid-cols-2 sm:grid-cols-5 gap-4">
          <div className="bg-white rounded-2xl shadow-sm p-4 flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-amber-50 flex items-center justify-center">
              <Coins className="w-5 h-5 text-amber-500" />
            </div>
            <div>
              <p className="text-xs text-[#6B7280]">Available</p>
              <p className="text-lg font-bold text-[#111827] tabular-nums">{balance.toLocaleString()}</p>
            </div>
          </div>
          <div className="bg-white rounded-2xl shadow-sm p-4 flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-green-50 flex items-center justify-center">
              <Coins className="w-5 h-5 text-[#10B981]" />
            </div>
            <div>
              <p className="text-xs text-[#6B7280]">Lifetime</p>
              <p className="text-lg font-bold text-[#111827] tabular-nums">{lifetimePoints.toLocaleString()}</p>
            </div>
          </div>
          <div className="bg-white rounded-2xl shadow-sm p-4 flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-orange-50 flex items-center justify-center">
              <Clock className="w-5 h-5 text-[#F97316]" />
            </div>
            <div>
              <p className="text-xs text-[#6B7280]">Streak</p>
              <p className="text-lg font-bold text-[#111827] tabular-nums">{(me?.streak_current ?? 0).toLocaleString()}</p>
            </div>
          </div>
          <Link to="/trust-center" className="bg-white rounded-2xl shadow-sm p-4 flex items-center gap-3 hover:shadow-md transition-all">
            <div className="w-10 h-10 rounded-xl bg-[#EEF2FF] flex items-center justify-center">
              <Shield className="w-5 h-5 text-[#5B4FE8]" />
            </div>
            <div>
              <p className="text-xs text-[#6B7280]">Trust</p>
              <p className="text-lg font-bold text-[#111827]">{trustLevelName}</p>
            </div>
          </Link>
          <Link
            to="/earning-history"
            className="bg-white rounded-2xl shadow-sm p-4 flex items-center gap-3 hover:shadow-md transition-all"
          >
            <div className="w-10 h-10 rounded-xl bg-[#F0FDF4] flex items-center justify-center">
              <ClipboardList className="w-5 h-5 text-[#10B981]" />
            </div>
            <div>
              <p className="text-xs text-[#6B7280]">Earnings</p>
              <p className="text-lg font-bold text-[#111827]">History</p>
            </div>
          </Link>
        </div>

        {/* Two column: Activity + Profile Strength */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <div className="bg-white rounded-2xl shadow-sm p-6">
            <h3 className="text-sm font-bold text-[#111827] uppercase tracking-wide mb-3">Recent Activity</h3>
            {recentActivity.length === 0 ? (
              <p className="text-sm text-[#6B7280] text-center py-4">No activity yet</p>
            ) : (
              <div className="space-y-2">
                {recentActivity.map((entry) => (
                  <div key={entry.id} className="flex items-center justify-between py-2 border-b border-gray-50 last:border-0">
                    <span className="text-sm text-[#111827]">{entry.note ?? ledgerLabel(entry.source_type)}</span>
                    <span className={`text-sm font-medium tabular-nums ${entry.delta >= 0 ? 'text-[#10B981]' : 'text-[#EF4444]'}`}>
                      {entry.delta >= 0 ? '+' : ''}{entry.delta}
                    </span>
                  </div>
                ))}
              </div>
            )}
          </div>
          <div>
            {isLoading ? (
              <div className="bg-white rounded-2xl shadow-sm p-6 h-40 animate-pulse" />
            ) : completion ? (
              <ProfileStrengthRing completion={completion} />
            ) : null}
          </div>
        </div>

        {/* Profile form sections */}
        <div className="space-y-6">
          <Section title="Demographics">
            <FieldRow label="Date of Birth" attrKey="date_of_birth" type="date" onSave={handleSave} inputProps={{ max: maxDOB, min: minDOB }} initialValue={attrMap['date_of_birth']} locked={me?.onboarding_complete} />
            <FieldRow label="Gender" attrKey="gender" type="select" options={[
              { value: 'male', label: 'Male' }, { value: 'female', label: 'Female' },
              { value: 'non_binary', label: 'Non binary' }, { value: 'prefer_not_to_say', label: 'Prefer not to say' },
            ]} onSave={handleSave} initialValue={attrMap['gender']} locked={me?.onboarding_complete} />
            <FieldRow label="ZIP Code" attrKey="zip_code" onSave={handleSave} inputProps={{ maxLength: 5, pattern: '[0-9]{5}' }} initialValue={attrMap['zip_code']} />
            <FieldRow label="Race / Ethnicity" attrKey="race" type="select" options={RACE_OPTIONS} onSave={handleSave} initialValue={attrMap['race']} locked={me?.onboarding_complete} />
            <FieldRow label="Hispanic Origin" attrKey="hispanic_origin" type="select" options={[
              { value: 'true', label: 'Yes' },
              { value: 'false', label: 'No' },
            ]} onSave={handleSave} initialValue={attrMap['hispanic_origin']} locked={me?.onboarding_complete} />
            <FieldRow label="Education" attrKey="education" type="select" options={[
              { value: 'some_high_school', label: 'Some High School' },
              { value: 'high_school_grad', label: 'High School Graduate' },
              { value: 'some_college', label: 'Some College' },
              { value: 'bachelors', label: "Bachelor's Degree" },
              { value: 'masters', label: "Master's Degree" },
              { value: 'doctorate', label: 'Doctorate' },
            ]} onSave={handleSave} initialValue={attrMap['education']} />
            {/* Canonical key: employment_status. Options sourced from the
                shared canonical list so drift vs attributeSchema.ts is caught
                at test time. */}
            <FieldRow label="Employment" attrKey="employment_status" type="select"
              options={EMPLOYMENT_FIELD_OPTIONS}
              onSave={handleSave}
              initialValue={attrMap['employment_status'] ?? attrMap['employment']} />
            {/* Canonical key: language_at_home. Enrichment key in the tiered
                score (KEY_TO_SECTION → demographic). Previously only fillable via
                survey-flow enrichment; opening it here makes the 90% tier
                reachable from the profile page. */}
            <FieldRow label="Language at Home" attrKey="language_at_home" type="select"
              options={LANGUAGE_FIELD_OPTIONS}
              onSave={handleSave}
              initialValue={attrMap['language_at_home']} unconfirmed={isInferred('language_at_home')} />
          </Section>

          <Section title="Household">
            <FieldRow label="Household Size" attrKey="household_size" type="number" onSave={handleSave} initialValue={attrMap['household_size']} />
            {/* Canonical key: hhi (numeric lower bound). Values above are the
                band floors that onboarding stores, so submissions round-trip
                exactly. Stored numbers are mapped back to the nearest band
                on read so members always see the right bracket selected. */}
            <FieldRow
              label="Income Band"
              attrKey="hhi"
              type="select"
              options={HHI_FIELD_OPTIONS}
              onSave={handleSave}
              initialValue={hhiToBandValue(attrMap['hhi'])}
            />
            <FieldRow label="Marital Status" attrKey="marital_status" type="select" options={[
              { value: 'single', label: 'Single' }, { value: 'engaged', label: 'Engaged' },
              { value: 'living_with_partner', label: 'Living with Partner' },
              { value: 'married', label: 'Married' }, { value: 'divorced', label: 'Divorced' },
              { value: 'widowed', label: 'Widowed' },
            ]} onSave={handleSave} initialValue={attrMap['marital_status']} />
            {/* Canonical key: military_status. Onboarding step 4 collects
                this and PS qual 1637 reads it. Was previously stored but
                not editable post-onboarding. */}
            <FieldRow label="Military Status" attrKey="military_status" type="select"
              options={MILITARY_STATUS_FIELD_OPTIONS}
              onSave={handleSave}
              initialValue={attrMap['military_status']} />
            <FieldRow label="Has Children" attrKey="has_children" type="select" options={[
              { value: 'yes', label: 'Yes' }, { value: 'no', label: 'No' },
            ]} onSave={handleSave} initialValue={attrMap['has_children']} unconfirmed={isInferred('has_children')} />
            {hasChildren === 'yes' && (
              <ChildrenManager initialValue={attrMap['child_birth_years']} fallback={me?.children} onSave={handleSave} />
            )}
          </Section>

          <Section title="Lifestyle & Interests">
            <ChipPicker label="Interests" options={INTERESTS_FIELD_OPTIONS} max={5} attrKey="interests" initialValue={attrMap['interests']} onSave={handleSave} />
            <ChipPicker label="Hobbies" options={HOBBIES_FIELD_OPTIONS} max={5} attrKey="hobbies" initialValue={attrMap['hobbies']} onSave={handleSave} />
            <FieldRow label="Political Leaning" attrKey="political_leaning" type="select" options={[
              { value: 'very_liberal', label: 'Very liberal' }, { value: 'liberal', label: 'Liberal' },
              { value: 'moderate', label: 'Moderate' }, { value: 'conservative', label: 'Conservative' },
              { value: 'very_conservative', label: 'Very conservative' },
              { value: 'prefer_not_to_say', label: 'Prefer not to say' },
            ]} onSave={handleSave} initialValue={attrMap['political_leaning']} />
            {/* Canonical key: voter_registration (boolean yes/no). Enrichment
                key in the tiered score (KEY_TO_SECTION → psychographic). */}
            <FieldRow label="Registered to Vote" attrKey="voter_registration" type="select" options={[
              { value: 'yes', label: 'Yes' }, { value: 'no', label: 'No' },
            ]} onSave={handleSave} initialValue={attrMap['voter_registration']} unconfirmed={isInferred('voter_registration')} />
            {/* Canonical key: social_media_use (multi_select). Stored comma-joined
                canonical values — MultiSelectRow saves on `value`, not label. */}
            <MultiSelectRow label="Social Media" attrKey="social_media_use"
              options={SOCIAL_MEDIA_FIELD_OPTIONS}
              onSave={handleSave}
              initialValue={attrMap['social_media_use']} unconfirmed={isInferred('social_media_use')} />
          </Section>

          {/* Email-frequency only makes sense with an email on file. Phone-
              registered members have email='' (empty string, NOT null) — a
              truthiness check treats both '' and null/undefined as "no email".
              Show an add-email affordance instead until a real email exists. */}
          {memberHasEmail(me?.member?.email)
            ? <EmailPreferencesSection currentFrequency={me?.email_survey_frequency ?? 'few_times_week'} />
            : <AddEmailSection />}
          <PhoneSection phoneNumber={me?.phone_number ?? null} phoneVerified={me?.phone_verified ?? false} smsOptedIn={me?.sms_opted_in ?? false} />

          <Section title="Home & Habits">
            <FieldRow label="Housing" attrKey="home_ownership" type="select" options={[
              { value: 'own', label: 'Own' }, { value: 'rent', label: 'Rent' },
              { value: 'live_with_family', label: 'Live with family' },
              { value: 'student_housing', label: 'Student housing' }, { value: 'other', label: 'Other' },
            ]} onSave={handleSave} initialValue={attrMap['home_ownership']} unconfirmed={isInferred('home_ownership')} />
            {/* Canonical key: auto_type. Options sourced from the shared
                canonical list — includes 'electric_hybrid' as a single value
                distinct from 'electric', matching attributeSchema.ts. */}
            <MultiSelectRow label="Vehicle(s)" attrKey="auto_type"
              options={AUTO_TYPE_FIELD_OPTIONS}
              onSave={handleSave}
              initialValue={attrMap['auto_type'] ?? attrMap['vehicle_type'] ?? attrMap['vehicle_ownership']}
              unconfirmed={isInferred('auto_type')} />
            {/* Canonical key: auto_make. Enrichment-only field added with
                the 2026-05-11 incentive redesign — onboarding doesn't ask;
                filling it on the profile page lifts the tiered score. Hidden
                when the member has no vehicle ('none') — a car make is
                meaningless then (and would confuse a non-owner). */}
            {showsCarMake(attrMap['auto_type'] ?? attrMap['vehicle_type'] ?? attrMap['vehicle_ownership']) && (
              <FieldRow label="Car Make" attrKey="auto_make" type="select"
                options={AUTO_MAKE_FIELD_OPTIONS}
                onSave={handleSave}
                initialValue={attrMap['auto_make']} unconfirmed={isInferred('auto_make')} />
            )}
            {/* Canonical key: pet_ownership. */}
            <MultiSelectRow label="Pets" attrKey="pet_ownership"
              options={PET_OWNERSHIP_FIELD_OPTIONS}
              onSave={handleSave}
              initialValue={attrMap['pet_ownership'] ?? attrMap['pets']}
              unconfirmed={isInferred('pet_ownership')} />
            {/* Canonical key: smoking_status. Onboarding step 4 collects this
                and PS qual 2653 reads it. Was previously stored but not
                editable post-onboarding. */}
            <FieldRow label="Smoking Status" attrKey="smoking_status" type="select"
              options={SMOKING_STATUS_FIELD_OPTIONS}
              onSave={handleSave}
              initialValue={attrMap['smoking_status']} unconfirmed={isInferred('smoking_status')} />
            {/* Canonical key: smartphone_type. The lone score key with no fill
                path before this — already in profile.ts VALID_KEYS, just lacked a
                widget (KEY_TO_SECTION → behavioral). */}
            <FieldRow label="Smartphone" attrKey="smartphone_type" type="select"
              options={SMARTPHONE_FIELD_OPTIONS}
              onSave={handleSave}
              initialValue={attrMap['smartphone_type']} unconfirmed={isInferred('smartphone_type')} />
          </Section>
        </div>
      </motion.div>

      {avatarModalOpen && (
        <AvatarModal
          currentId={avatarId}
          onSelect={(id) => void handleAvatarSelect(id)}
          onClose={() => setAvatarModalOpen(false)}
        />
      )}
    </NavShell>
  );
}
